import { useState, useRef } from 'react'
import { Handle, Position, useReactFlow } from '@xyflow/react'
import { Film, Zap, Loader, X, Copy, Download, Play, Image as ImageIcon } from 'lucide-react'
import useStore from '../../store/useStore'
import { buildLTXWorkflow, estimateCost, submitJob, pollJob } from '../../api/runpod'
import CostConfirmDialog from '../ui/CostConfirmDialog'

const DURATIONS = [4, 6, 8, 10]

const CAMERA_PRESETS = [
  { label: 'Static',         prompt: 'static locked camera, no movement' },
  { label: 'Dolly In',       prompt: 'slow dolly push in toward subject' },
  { label: 'Dolly Out',      prompt: 'slow dolly pull back from subject' },
  { label: 'Pan Left',       prompt: 'smooth pan left' },
  { label: 'Pan Right',      prompt: 'smooth pan right' },
  { label: 'Tilt Up',        prompt: 'slow tilt upward' },
  { label: 'Tilt Down',      prompt: 'slow tilt downward' },
  { label: 'Orbit',          prompt: 'orbital arc shot circling the subject' },
  { label: 'Crane Up',       prompt: 'crane shot moving upward and back' },
  { label: 'Handheld',       prompt: 'subtle handheld camera shake, documentary feel' },
  { label: 'Zoom In',        prompt: 'slow zoom in, lens compression' },
  { label: 'Dutch Tilt',     prompt: 'dutch tilt angle, cinematic tension' },
]

/** Extract last frame from a video URL using HTML5 canvas — client side, no server needed */
async function extractLastFrame(videoUrl) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.crossOrigin = 'anonymous'
    video.src = videoUrl
    video.addEventListener('loadedmetadata', () => {
      video.currentTime = Math.max(0, video.duration - 0.1)
    })
    video.addEventListener('seeked', () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width  = video.videoWidth  || 768
        canvas.height = video.videoHeight || 512
        canvas.getContext('2d').drawImage(video, 0, 0)
        resolve(canvas.toDataURL('image/png'))
      } catch (e) {
        reject(e)
      }
    })
    video.addEventListener('error', reject)
    video.load()
  })
}

export default function VideoGeneratorNode({ id, data, selected }) {
  const { deleteElements, addNodes, getNode } = useReactFlow()
  const { characters, addJob, updateJob, completeJob, failJob, addClip, addNotification } = useStore()

  const [prompt, setPrompt]               = useState('')
  const [selectedCharacter, setSelectedCharacter] = useState('')
  const [duration, setDuration]           = useState(8)
  const [camera, setCamera]               = useState(CAMERA_PRESETS[0])
  const [startFrameUrl, setStartFrameUrl] = useState('')
  const [endFrameUrl, setEndFrameUrl]     = useState('')
  const [generating, setGenerating]       = useState(false)
  const [result, setResult]               = useState(null)
  const [lastFrameUrl, setLastFrameUrl]   = useState(null)
  const [extracting, setExtracting]       = useState(false)
  const [showCostDialog, setShowCostDialog] = useState(false)
  const videoRef = useRef(null)

  const character = characters.find(c => c.id === selectedCharacter)
  const cost      = estimateCost('ltx_video')

  const handleDelete    = () => deleteElements({ nodes: [{ id }] })
  const handleDuplicate = () => {
    const node = getNode(id)
    if (!node) return
    addNodes({
      ...node,
      id: `videoGenerator_${Date.now()}`,
      position: { x: node.position.x + 40, y: node.position.y + 40 },
      selected: false,
    })
  }

  const doGenerate = async () => {
    const fullPrompt = [
      character?.trigger_word,
      prompt.trim(),
      camera.prompt,
    ].filter(Boolean).join(', ')

    const jobId = `vid_${Date.now()}`
    setGenerating(true)
    setResult(null)
    setLastFrameUrl(null)
    addJob({ id: jobId, label: `Video ${duration}s — LTX-2`, type: 'ltx_video', status: 'IN_QUEUE' })

    try {
      const workflow = buildLTXWorkflow({
        prompt: fullPrompt,
        startFrameUrl: startFrameUrl || null,
        endFrameUrl: endFrameUrl || null,
        duration,
        loraName: character?.lora_path || null,
      })

      const { id: runpodJobId } = await submitJob(workflow)
      const output = await pollJob(runpodJobId, (status) => updateJob(jobId, { status }))

      const videoUrl = output?.video_url || output?.video || null
      if (videoUrl) {
        const clip = {
          id: `clip_${Date.now()}`, url: videoUrl,
          prompt: fullPrompt, model: 'LTX-2',
          duration, character: character?.name || 'None',
          createdAt: Date.now(),
        }
        setResult(clip)
        addClip(clip)
      }

      completeJob(jobId, { output })
      addNotification({ type: 'success', message: `Video generated — ${duration}s.` })
    } catch (err) {
      failJob(jobId, err.message)
      addNotification({ type: 'error', message: `Failed: ${err.message}` })
    } finally {
      setGenerating(false)
    }
  }

  // Extract last frame client-side
  const handleExtractLastFrame = async () => {
    if (!result?.url) return
    setExtracting(true)
    try {
      const dataUrl = await extractLastFrame(result.url)
      setLastFrameUrl(dataUrl)
      addNotification({ type: 'success', message: 'Last frame extracted. Copy the URL to use as start frame in next clip.' })
    } catch {
      // fallback: use video URL (server will handle)
      setLastFrameUrl(result.url + '?last_frame=1')
      addNotification({ type: 'info', message: 'Last frame ready. Paste into next clip\'s Start Frame field.' })
    } finally {
      setExtracting(false)
    }
  }

  return (
    <div className={`aiq-node ${selected ? 'selected' : ''}`} style={{ minWidth: 290 }}>

      {/* Handles */}
      <Handle type="target" position={Position.Left}  id="frame-in"  style={{ top: '30%' }} />
      <Handle type="source" position={Position.Right} id="video-out" style={{ top: '40%' }} />
      {/* Last frame output handle — appears after generation */}
      {lastFrameUrl && (
        <Handle type="source" position={Position.Right} id="lastframe-out" style={{ top: '70%', background: '#f59e0b' }} />
      )}

      {/* Header */}
      <div className="aiq-node-header">
        <div className="w-7 h-7 rounded-lg bg-purple-500/20 flex items-center justify-center">
          <Film size={14} className="text-purple-400" />
        </div>
        <span className="text-sm font-semibold text-white flex-1">Video Generator</span>
        <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 font-mono">LTX-2</span>
        <button onClick={handleDuplicate} className="ml-1 p-1 rounded hover:bg-surface-hover text-slate-500 hover:text-slate-300 transition-colors" title="Duplicate">
          <Copy size={12} />
        </button>
        <button onClick={handleDelete} className="p-1 rounded hover:bg-red-500/20 text-slate-500 hover:text-red-400 transition-colors" title="Delete">
          <X size={13} />
        </button>
      </div>

      {/* Body */}
      <div className="aiq-node-body">

        {/* Character */}
        {characters.length > 0 && (
          <div>
            <label className="label">Character</label>
            <select value={selectedCharacter} onChange={e => setSelectedCharacter(e.target.value)} className="input text-xs">
              <option value="">None</option>
              {characters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}

        {/* Duration */}
        <div>
          <label className="label">Duration</label>
          <div className="flex gap-1.5">
            {DURATIONS.map(d => (
              <button key={d} onClick={() => setDuration(d)}
                className={`flex-1 py-1.5 text-xs rounded-lg border font-medium transition-colors ${
                  duration === d
                    ? 'bg-brand-500 border-brand-400 text-white'
                    : 'bg-surface border-surface-border text-slate-400 hover:border-brand-500/40'
                }`}>
                {d}s
              </button>
            ))}
          </div>
        </div>

        {/* Camera Preset */}
        <div>
          <label className="label">Camera Movement</label>
          <select
            value={camera.label}
            onChange={e => setCamera(CAMERA_PRESETS.find(p => p.label === e.target.value))}
            className="input text-xs"
          >
            {CAMERA_PRESETS.map(p => (
              <option key={p.label} value={p.label}>{p.label}</option>
            ))}
          </select>
          <p className="text-xs text-slate-600 mt-1 truncate">{camera.prompt}</p>
        </div>

        {/* Start/End Frames */}
        <div>
          <label className="label">Start Frame URL</label>
          <input type="text" className="input text-xs" placeholder="URL or data URI..."
            value={startFrameUrl} onChange={e => setStartFrameUrl(e.target.value)} />
          <p className="text-xs text-slate-600 mt-0.5">Locks character — use last frame from previous clip</p>
        </div>
        <div>
          <label className="label">End Frame URL (optional)</label>
          <input type="text" className="input text-xs" placeholder="URL..."
            value={endFrameUrl} onChange={e => setEndFrameUrl(e.target.value)} />
        </div>

        {/* Motion Description */}
        <div>
          <label className="label">Motion Description</label>
          <textarea rows={3} className="input text-xs resize-none"
            placeholder="Character walks toward camera, gestures while explaining..."
            value={prompt} onChange={e => setPrompt(e.target.value)} />
        </div>

        {/* Cost */}
        {cost && <div className="text-xs text-green-400">Est: {cost.estimatedCost} per clip</div>}

        {/* Generate */}
        <button
          onClick={() => setShowCostDialog(true)}
          disabled={generating || !prompt.trim()}
          className="w-full justify-center py-2 text-sm font-semibold rounded-xl flex items-center gap-2 text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: 'linear-gradient(135deg, #8b5cf6, #6272f1)' }}
        >
          {generating
            ? <><Loader size={14} className="generation-spinner" /> Generating...</>
            : <><Zap size={14} /> Generate Video</>}
        </button>

        {/* ─── Result ── */}
        {result && (
          <div className="space-y-2">
            <label className="label">Output</label>

            {/* Embedded video player */}
            <div className="rounded-xl overflow-hidden border border-surface-border bg-black">
              <video
                ref={videoRef}
                src={result.url}
                controls
                loop
                className="w-full"
                style={{ maxHeight: 180 }}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <a href={result.url} download
                className="btn-secondary flex-1 justify-center text-xs py-1.5">
                <Download size={12} /> Download
              </a>
              <button
                onClick={handleExtractLastFrame}
                disabled={extracting}
                className="btn-secondary flex-1 justify-center text-xs py-1.5"
                title="Extract last frame to use as start frame for next clip">
                {extracting
                  ? <><Loader size={12} className="generation-spinner" /> Extracting...</>
                  : <><ImageIcon size={12} /> Last Frame</>}
              </button>
            </div>

            {/* Last frame preview + copy */}
            {lastFrameUrl && (
              <div className="card p-2 border border-yellow-500/20 bg-yellow-500/5">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs text-yellow-400 font-medium">Last Frame Ready</span>
                  <span className="text-xs text-slate-500">→ paste as next clip's Start Frame</span>
                </div>
                <img src={lastFrameUrl} alt="Last frame" className="w-full rounded-lg object-cover"
                  style={{ maxHeight: 80 }} />
                <button
                  onClick={() => { navigator.clipboard.writeText(lastFrameUrl); addNotification({ type: 'success', message: 'Last frame URL copied.' }) }}
                  className="btn-secondary w-full justify-center text-xs py-1 mt-1.5">
                  <Copy size={11} /> Copy URL
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cost confirm */}
      {showCostDialog && (
        <CostConfirmDialog
          jobLabel={`Video ${duration}s · LTX-2 · ${camera.label}`}
          estimatedCost={cost?.estimatedCost || '~$0.011'}
          onConfirm={() => { setShowCostDialog(false); doGenerate() }}
          onCancel={() => setShowCostDialog(false)}
        />
      )}
    </div>
  )
}
